import React, { FC, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { ApiMessageStack, FormControlLabel, FormControlV2 } from "components";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { observer } from "mobx-react";
import {
  TAccountIntegrationData,
  TStreetConfig,
} from "types/account-integration.type";
import * as Yup from "yup";
import { REQUIRED_FIELD } from "constants/validator-messages";
import { ToggleIntegrationActiveSwitchInput } from "../ToggleIntegrationActiveSwitchInput";
import {
  streetIntegrationConnectValidationSchema,
  TStreetIntegrationConfigFieldValues,
} from "utils/validation-schemas/street-integration-validation";

type TFieldValues = TStreetIntegrationConfigFieldValues & {
  active: boolean;
};

const validationSchema = streetIntegrationConnectValidationSchema.concat(
  Yup.object().shape({
    active: Yup.boolean().required(REQUIRED_FIELD),
  })
);

interface IStreetFormProps {
  accountIntegrationsStore: AccountIntegrationsStore;
}

export const StreetIntegrationForm: FC<IStreetFormProps> = observer(
  ({ accountIntegrationsStore }) => {
    const toast = useToast();
    const accountIntegration = accountIntegrationsStore
      .accountIntegrationsMapByType[
      AccountIntegrationTypeEnum.STREET
    ] as TAccountIntegrationData<TStreetConfig>;

    const initialValues = useMemo(() => {
      return {
        token: accountIntegration.integrationConfig.token.toString(),
        testMode: accountIntegration.integrationConfig.testMode,
        active: accountIntegration.active,
      };
    }, [accountIntegration]);

    const {
      handleSubmit,
      watch,
      control,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const { active } = watch();

    const handleUpdateIntegrationConfig = useCallback(
      async ({ active, ...rest }: TFieldValues) => {
        try {
          await accountIntegrationsStore.updateAccountIntegration<
            TStreetIntegrationConfigFieldValues
          >(accountIntegration.id, {
            type: accountIntegration.type,
            active: active,
            integrationConfig: rest,
          });
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={"Street integration configuration updated."}
              />
            ),
          });
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [
        accountIntegration.id,
        accountIntegration.type,
        accountIntegrationsStore,
        toast,
      ]
    );

    return (
      <Box w={"100%"}>
        <form onSubmit={handleSubmit(handleUpdateIntegrationConfig)}>
          <ToggleIntegrationActiveSwitchInput<TFieldValues>
            label={"Connecting with Street"}
            control={control}
            value={active}
            name={"active"}
          />
          <Box mb={5}>
            <FormControlV2<TFieldValues>
              label={"Token"}
              type={FormControlsTypeEnum.TEXT}
              name={"token"}
              control={control}
            />
          </Box>
          <Box
            mb={5}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <FormControlLabel>Test mode</FormControlLabel>
            <FormControlV2<TFieldValues>
              name={"testMode"}
              control={control}
              type={FormControlsTypeEnum.SWITCH}
            />
          </Box>
          <Divider mb={4} />
          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              type={"submit"}
              colorScheme={"blue"}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>
    );
  }
);
